import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  sessionNext, submitAnswer, endSession, startSubtopic, saveAndExitSession, tellMeMore,
} from "@/lib/api";
import ReviseRecapRenderer from "@/components/ReviseRecapRenderer";
import QuestionCard from "@/components/QuestionCard";
import ExplanationCard from "@/components/ExplanationCard";
import GuidedReview from "@/components/GuidedReview";
import StopScreen from "@/components/StopScreen";
import CompleteScreen from "@/components/CompleteScreen";
import PoolACompleteScreen from "@/components/PoolACompleteScreen";
import RecapToWarmUpScreen from "@/components/RecapToWarmUpScreen";
import WarmUpToPracticeScreen from "@/components/WarmUpToPracticeScreen";
import ReEntryScreen from "@/components/ReEntryScreen";
import ProgressRing from "@/components/ProgressRing";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { useRole } from "@/contexts/RoleContext";

/**
 * Single page — drives engine action loop. The Engine decides what comes next.
 */
export default function SessionRunner() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { learner } = useRole();
  const initialAction = location.state?.initialAction || null;

  // Re-entry context — set once from router state, never changes
  const reEntrySubject   = location.state?.subject   || null;
  const reEntrySub_topic = location.state?.sub_topic || null;

  const [action, setAction]         = useState(initialAction);
  const [loading, setLoading]       = useState(!initialAction && !location.state?.resumed);
  const [reviewing, setReviewing]   = useState(null);
  const [lastQuestion, setLastQuestion] = useState(null);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [completedSubtopic, setCompletedSubtopic] = useState(false);
  const [showReEntry, setShowReEntry] = useState(!!location.state?.resumed);
  // Guard so React StrictMode double-mounts don't double-call /next on initial load.
  const initialFetchDone   = useRef(false);
  // True when we're in a resumed session and auto-fetch must wait for user choice.
  const isReEntry          = useRef(!!location.state?.resumed);
  // One-time flag: skip the recap_to_warmup transition screen after a re-entry recap.
  const skipRecapTransition = useRef(false);

  const fetchNext = useCallback(async () => {
    setLoading(true);
    try {
      let a = await sessionNext(sessionId);
      if (a.type === "complete" && a.payload?.after_stop) {
        const subj = a.payload?.subject;
        nav(subj ? `/subject/${encodeURIComponent(subj)}` : "/");
        return;
      }
      // After a re-entry recap, silently skip the recap_to_warmup transition screen
      // and serve the first warmup question directly.
      if (a.type === "recap_to_warmup" && skipRecapTransition.current) {
        skipRecapTransition.current = false;
        a = await sessionNext(sessionId);
      }
      setAction(a);
      if (a.type === "complete") setCompletedSubtopic(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId, nav]);

  useEffect(() => {
    // Only auto-fetch when there's no initial action forwarded from the previous page,
    // and this is not a re-entry (resumed) session waiting for user choice.
    if (!initialAction && !isReEntry.current && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchNext();
    }
  }, [fetchNext, initialAction]);

  // Re-entry handlers — called by ReEntryScreen buttons
  const onResume = useCallback(() => {
    isReEntry.current = false;
    setShowReEntry(false);
    fetchNext();
  }, [fetchNext]);

  const onRevisitRecap = useCallback(async () => {
    if (!reEntrySubject || !reEntrySub_topic) return;
    setLoading(true);
    try {
      const recapAction = await startSubtopic(sessionId, reEntrySubject, reEntrySub_topic);
      skipRecapTransition.current = true;
      initialFetchDone.current = true;
      isReEntry.current = false;
      setShowReEntry(false);
      setAction(recapAction);
    } finally {
      setLoading(false);
    }
  }, [sessionId, reEntrySubject, reEntrySub_topic]);

  const onAnswer = async (answer, seconds) => {
    if (!action) return;
    const q = action.payload.question;
    setLastQuestion(q);
    setLastAnswer(answer);
    setLoading(true);
    try {
      const next = await submitAnswer(sessionId, q.record_id, answer, seconds);
      setAction(next);
    } finally {
      setLoading(false);
    }
  };

  const onExplanationNext = async () => {
    await fetchNext();
  };

  const onReviewMistake = () => {
    if (!lastQuestion) return;
    setReviewing(lastQuestion.record_id);
    // Pre-warm the "Ask Mitraa to simplify" cache so it renders instantly at Step 4.
    // Fire-and-forget — errors are silently swallowed; the button will fall back to a live call.
    tellMeMore({
      scope: "guided_review_step4",
      recordId: lastQuestion.record_id,
      learnerId: learner?.learner_id,
      wrongOptionChosen: lastAnswer,
    }).catch(() => {});
  };

  const onReviewDone = async () => {
    setReviewing(null);
    await fetchNext();
  };

  const onStopContinue = () => {
    const subj = action?.payload?.reroute?.subject || action?.payload?.subject;
    nav(subj ? `/subject/${encodeURIComponent(subj)}` : "/home");
  };

  const onSubtopicNext = () => {
    nav("/");
  };

  const onEndSession = async () => {
    await endSession(sessionId);
    setAction({ type: "complete", payload: { summary: "Wonderful session — see you next time!", session_done: true } });
    setCompletedSubtopic(false);
  };

  const onSaveAndExit = async () => {
    try {
      await saveAndExitSession(sessionId);
    } catch { /* server-side state already saved on every action */ }
    // Send the learner back to the subject overview so the Resume pill appears.
    const subj = action?.payload?.question?.subject || action?.payload?.subject;
    if (subj) {
      nav(`/subject/${encodeURIComponent(subj)}`);
    } else {
      nav("/");
    }
  };

  // Save & exit is only meaningful during an active answering phase — not on
  // recap landing, mid-explanation, STOP screen, complete screen, or guided review.
  const canSaveExit = !loading && !reviewing && (action?.type === "warmup" || action?.type === "question");

  // Compute progress for header ring
  const progress = action?.payload?.progress;

  if (reviewing) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <GuidedReview
          sessionId={sessionId}
          recordId={reviewing}
          subject={lastQuestion?.subject || action?.payload?.subject}
          wrongOptionChosen={lastAnswer}
          onDone={onReviewDone}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="session-runner">
      <div className="flex items-center justify-between mb-4 gap-3">
        {canSaveExit ? (
          <Button
            type="button"
            variant="outline"
            onClick={onSaveAndExit}
            className="rounded-full border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold tap-44"
            data-testid="save-exit-btn"
          >
            <Save className="w-4 h-4 mr-1.5" /> Save &amp; exit
          </Button>
        ) : <span />}
        {progress && (
          <div className="flex items-center gap-3" data-testid="session-progress">
            <ProgressRing value={progress.current} total={progress.total} size={56} stroke={5} />
            <div className="text-xs uppercase font-semibold text-slate-500">{progress.layer}</div>
          </div>
        )}
      </div>

      {loading && <div className="text-center text-slate-400 py-12" data-testid="loading-action">Loading…</div>}

      {!loading && showReEntry && (
        <ReEntryScreen
          sub_topic={reEntrySub_topic}
          onRevisitRecap={onRevisitRecap}
          onResume={onResume}
        />
      )}

      {!loading && !showReEntry && action?.type === "recap"       && <ReviseRecapRenderer payload={action.payload} onProceed={fetchNext} />}
      {!loading && !showReEntry && action?.type === "recap_to_warmup" && <RecapToWarmUpScreen payload={action.payload} onProceed={fetchNext} />}
      {!loading && !showReEntry && action?.type === "warmup"      && <QuestionCard question={action.payload.question} onSubmit={onAnswer} layerLabel="WarmUp" progress={action.payload.progress} moment={action.payload.mitraa_moment} />}
      {!loading && !showReEntry && action?.type === "warmup_to_practice" && <WarmUpToPracticeScreen payload={action.payload} onProceed={fetchNext} />}
      {!loading && !showReEntry && action?.type === "question"    && <QuestionCard question={action.payload.question} onSubmit={onAnswer} layerLabel={`${action.payload.pool === "Pool B" ? "Challenge" : "Practice"}${action.payload.position === "buffer" ? " • Buffer" : ""}`} progress={action.payload.progress} moment={action.payload.mitraa_moment} />}
      {!loading && !showReEntry && action?.type === "explanation" && <ExplanationCard payload={action.payload} onNext={onExplanationNext} onReviewMistake={onReviewMistake} sessionId={sessionId} />}
      {!loading && !showReEntry && action?.type === "stop"        && <StopScreen payload={action.payload} onContinue={onStopContinue} />}
      {!loading && !showReEntry && action?.type === "pool_a_complete" && (
        <PoolACompleteScreen payload={action.payload} onProceed={fetchNext} />
      )}
      {!loading && !showReEntry && action?.type === "complete" && completedSubtopic && (
        <CompleteScreen payload={action.payload} mode="subtopic" onNext={onSubtopicNext} onEndSession={onEndSession} />
      )}
      {!loading && !showReEntry && action?.type === "complete" && !completedSubtopic && (
        <CompleteScreen payload={action.payload} mode="session" />
      )}
    </div>
  );
}
